import React, { useState, useEffect } from 'react';
import {
  Grid,
  Box,
  Typography,
  Button,
  Card,
  Divider,
  Dialog,
  DialogTitle,
  DialogActions,
  IconButton,
  useTheme,
  Skeleton,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PendingIcon from '@mui/icons-material/Pending';
import WarningIcon from '@mui/icons-material/Warning';
import BlockIcon from '@mui/icons-material/Block';
import CircularProgress from '@mui/material/CircularProgress';
import AlertBox from '../../../components/AlertBox';

// Status types
export const STRIPE_STATUS = {
  NOT_CONNECTED:'not_connected',
  PENDING: 'Pending',
  RESTRICTED: 'Restricted',
  CONNECTED: 'Connected',
  DISABLED: 'Disabled',
  LOADING: 'loading',
  ERROR: 'error'
};

const StripeAccountStatus = ({  
  status = STRIPE_STATUS.LOADING,
  onConnect,
  onDisconnect,
  onManageAccount,
  isLoading = false,
  error = null,
  onRetry = null,
  stripeDashboardUrl = 'https://dashboard.stripe.com',
}) => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [localStatus, setLocalStatus] = useState(status);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [showInfoBanner, setShowInfoBanner] = useState(true);

  useEffect(() => {
    setLocalStatus(status);
    // Show banner for restricted and pending statuses by default
    setShowInfoBanner(
      status === STRIPE_STATUS.RESTRICTED || 
      status === STRIPE_STATUS.PENDING || 
      status === STRIPE_STATUS.NOT_CONNECTED
    );
  }, [status]);

  const handleOpen = () => setOpenDialog(true);
  const handleClose = () => setOpenDialog(false);

  const handleCloseInfoBanner = () => {
    setShowInfoBanner(false);
  };

  const handleManageClick = () => {
    handleClose();
    if (onManageAccount) {
      onManageAccount();
    }
  };

  const handleConnectClick = async () => {
    setActionInProgress(true);
    try {
      if (onConnect) {
        await onConnect();
      }
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDisconnectClick = async () => {
    setActionInProgress(true);
    try {
      if (onDisconnect) {
        await onDisconnect();
      }
    } finally {
      setActionInProgress(false);
    }
  };

  const handleRetryClick = () => {
    if (onRetry) {
      onRetry();
    }
  };

  // Status configurations
  const statusConfig = {
    [STRIPE_STATUS.NOT_CONNECTED]: {
      title: 'Connect Your Stripe Account',
      subtitle: 'To accept payments, you must connect your Stripe account. Please complete Stripe setup to continue.',
      icon: AddIcon,
      iconColor: '#3B82F6',
      bgColor: theme.palette.mode === 'dark' ? '#1E293B' : '#EFF6FF',
      textColor:  theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.78)':'#003A70'  ,
      buttonText: 'Connect Stripe Account',
      buttonVariant: 'contained',
      showManageButton: false,
      showConnectButton: true,
      alertSeverity: 'info',
      alertTitle: 'Stripe Not Connected',
      alertMessage: 'You need to connect a Stripe account to accept payments.',
      showBenefits: true,
      benefits: [
        'Accept credit card payments securely',
        'Automated payment processing',
        'PCI compliant checkout',
        'Detailed payment analytics'
      ],
      statusChip: null
    },
    [STRIPE_STATUS.PENDING]: {
      title: 'Stripe Account Pending',
      subtitle: 'Your Stripe account is under review. This usually takes 1-2 business days.',
      icon: PendingIcon,
      iconColor: '#F59E0B',
      bgColor: theme.palette.mode === 'dark' ? '#2A1F05' : '#FFFBEB',
      textColor: '#D97706',
      buttonText: 'Check Status',
      buttonVariant: 'outlined',
      showManageButton: false,
      alertSeverity: 'warning',
      alertTitle: 'Account Under Review',
      alertMessage: 'Your account is currently under review by Stripe. This process usually takes 1-2 business days.',
      statusChip: (
        <Chip
          icon={<PendingIcon />}
          label="Under Review"
          size="small"
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(245, 158, 11, 0.15)' : '#FEF3C7',
            color: '#D97706',
            fontWeight: 500,
            ml: 2,
            '& .MuiChip-icon': {
              color: '#D97706 !important'
            }
          }}
        />
      )
    },
    [STRIPE_STATUS.RESTRICTED]: {
      title: 'Action Required',
      subtitle: 'Additional information is needed to verify your Stripe account. Please update your details.',
      icon: WarningIcon,
      iconColor: '#DC2626',
      bgColor: theme.palette.mode === 'dark' ? '#2C1A1A' : '#FEF2F2',
      textColor: '#6B7280',
      topButtonText : 'Update Details',
      buttonText: 'Disconnect Account',
      buttonVariant: 'outlined',
      showManageButton: true,
      alertSeverity: 'error',
      alertTitle: 'Action Required',
      alertMessage: 'Please verify your business details, upload required documents, and ensure your account information is complete.',
      statusChip: (
        <Chip
          icon={<WarningIcon />}
          label="Action Required"
          size="small"
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(220, 38, 38, 0.15)' : '#FEE2E2',
            color: '#B91C1C',
            fontWeight: 500,
            ml: 2,
            '& .MuiChip-icon': {
              color: '#B91C1C !important'
            }
          }}
        />
      )
    },
    [STRIPE_STATUS.CONNECTED]: {
      title: 'Stripe Connected Successfully!',
      subtitle: 'Your Stripe account has been successfully connected to our platform. You can now start accepting payments.',
      icon: CheckCircleOutlineIcon,
      iconColor: '#059669',
      bgColor: theme.palette.mode === 'dark' ? '#0F2A21' : '#ECFDF5',
      textColor: '#047857',
      buttonText: 'Manage Account',
      topButtonText: 'Manage Account',
      buttonVariant: 'outlined',
      showManageButton: true,
      alertSeverity: 'success',
      alertTitle: 'Account Active',
      alertMessage: 'Your Stripe account is active and ready to accept payments.',
      statusChip: (
        <Chip
          icon={<CheckCircleOutlineIcon />}
          label="Active"
          size="small"
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(5, 150, 105, 0.15)' : '#D1FAE5',
            color: '#047857',
            fontWeight: 500,
            ml: 2,
            '& .MuiChip-icon': {
              color: '#047857 !important'
            }
          }}
        />
      )
    },
    [STRIPE_STATUS.DISABLED]: {
      title: 'Account Disabled',
      subtitle: 'Your Stripe account has been disabled. Please reconnect your account.',
      icon: BlockIcon,
      iconColor: '#6B7280',
      bgColor: theme.palette.mode === 'dark' ? '#2A2A2A' : '#F3F4F6',
      textColor: '#4B5563',
      buttonText: 'Reconnect',
      buttonVariant: 'contained',
      showManageButton: false,
      alertSeverity: 'custom',
      alertTitle: 'Account Disabled',
      alertMessage: 'Your Stripe account has been disabled. ',
      statusChip: (
        <Chip
          icon={<BlockIcon />}
          label="Disabled"
          size="small"
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(107, 114, 128, 0.15)' : '#F3F4F6',
            color: '#4B5563',
            fontWeight: 500,
            ml: 2,
            '& .MuiChip-icon': {
              color: '#4B5563 !important'
            }
          }}
        />
      )
    },
    [STRIPE_STATUS.LOADING]: {
      title: 'Loading Stripe Status...',
      subtitle: 'Checking your Stripe account connection',
      icon: null,
      iconColor: '',
      bgColor: theme.palette.mode === 'dark' ? '#1B2431' : '#F5F5F5',
      textColor: theme.palette.text.secondary,
      buttonText: 'Connect',
      buttonVariant: 'contained',
      showManageButton: false,
      alertSeverity: 'info',
      alertTitle: '',
      alertMessage: ''
    },
    [STRIPE_STATUS.ERROR]: {
      title: 'Error Loading Status',
      subtitle: 'Unable to fetch Stripe account status. Please try again.',
      icon: WarningIcon,
      iconColor: '#DC2626',
      bgColor: theme.palette.mode === 'dark' ? '#2C1A1A' : '#FEF2F2',
      textColor: '#B91C1C',
      buttonText: 'Retry',
      buttonVariant: 'contained',
      showManageButton: false,
      alertSeverity: 'error',
      alertTitle: 'Connection Error',
      alertMessage: 'Unable to fetch Stripe account status. Please try again.'
    }
  };

  const config = statusConfig[localStatus] || statusConfig[STRIPE_STATUS.LOADING];

  // Loading skeleton
  if (isLoading || localStatus === STRIPE_STATUS.LOADING) {
    return (
      <Grid container spacing={5}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 2, sm: 0 },
              mb: 3
            }}
          >
            <Box>
              <Skeleton variant="text" width={200} height={40} />
              <Skeleton variant="text" width={300} height={24} sx={{ mt: 1 }} />
            </Box>
            <Skeleton variant="rectangular" width={120} height={40} />
          </Box>
          <Divider sx={{ mb: 4 }} />
          
          <Card
            sx={{
              p: 4,
              backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : '#F5F5F5',
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider',
              mb: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Skeleton variant="circular" width={50} height={50} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" height={32} />
                <Skeleton variant="text" width="80%" height={24} sx={{ mt: 1 }} />
              </Box>
            </Box>
          </Card>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Skeleton variant="rectangular" width={150} height={40} />
          </Box>
        </Grid>
      </Grid>
    );
  }

  // Error state
  if (error || localStatus === STRIPE_STATUS.ERROR) {
    return (
      <Grid container spacing={5}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 2, sm: 0 }
            }}
          >
            <Box>
              <Typography variant="h3">Stripe Account</Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                This is the method used to collect payments from your Customers.
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ pt: 2, mb: 4 }} />
          
          <AlertBox
            severity="error"
            title={config.alertTitle}
            onClose={handleCloseInfoBanner}
            sx={{ mb: 3 }}
          >
            {config.alertMessage}
          </AlertBox>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant={config.buttonVariant}
              onClick={handleRetryClick}
              disabled={actionInProgress}
              startIcon={actionInProgress ? <CircularProgress size={20} /> : null}
              sx={{
                backgroundColor: '#DC2626',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#B91C1C'
                }
              }}
            >
              {actionInProgress ? 'Retrying...' : config.buttonText}
            </Button>
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <Grid container spacing={5} sx={{ overflowX: 'auto' }}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 2, sm: 0 }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h3">Stripe Account</Typography>
              {config.statusChip}
            </Box>
            
            {config.showManageButton && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleOpen}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.secondary,
                    borderColor: theme.palette.divider,
                    '&:hover': {
                      borderColor: '#3B82F6',
                      color: '#3B82F6'
                    }
                  }}
                >
                  {config.topButtonText}
                </Button>
                
                {/* Manage Account Dialog */}
                <Dialog
                  open={openDialog}
                  onClose={handleClose}
                  maxWidth="xs"
                  fullWidth
                >
                  <DialogTitle sx={{ pb: 2, position: 'relative' }}>
                    <IconButton
                      onClick={handleClose}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: '#6B7280'
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                    <Typography variant="h5" sx={{ pr: 4 }}>
                      Manage Stripe Account
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2, pr: 2 }}
                    >
                      You will be redirected to your Stripe dashboard to manage your account settings.
                    </Typography>
                  </DialogTitle>
                  <Box sx={{ height: 24 }} />
                  <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                      variant="outlined"
                      onClick={handleClose}
                      sx={{
                        backgroundColor: '#fff',
                        color: '#6B7280',
                        borderColor: '#F3F4F6',
                        '&:hover': {
                          borderColor: '#6B7280',
                          backgroundColor: '#F3F4F6'
                        }
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleManageClick}
                      sx={{
                        backgroundColor: '#3B82F6',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: '#2563EB'
                        }
                      }}
                    >
                      Stripe Dashboard
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            )}
          </Box>
          
          <Typography color="text.secondary" sx={{ mt: 1.5, mb: 4 }}>
            This is the method used to collect payments from your Customers.
          </Typography>
          
          <Divider sx={{ mb: 4 }} />
        </Grid>
        
        <Grid item xs={12}>
          {/* Alert Banner for important status messages */}
          {/* {showInfoBanner && config.alertMessage && (
            <AlertBox
              severity={config.alertSeverity}
              title={config.alertTitle}
              onClose={handleCloseInfoBanner}
              sx={{ mb: 3 }}
            >
              {config.alertMessage}
            </AlertBox>
          )} */}

          {/* Status Card */}
          <Card
            sx={{
              p: 4,
              backgroundColor: config.bgColor,
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 
                localStatus === STRIPE_STATUS.RESTRICTED ? 'rgba(220, 38, 38, 0.2)' :
                localStatus === STRIPE_STATUS.PENDING ? 'rgba(245, 158, 11, 0.2)' :
                localStatus === STRIPE_STATUS.CONNECTED ? 'rgba(5, 150, 105, 0.2)' :
                localStatus === STRIPE_STATUS.DISABLED ? 'rgba(107, 114, 128, 0.2)' : 'divider',
              mb: 3,
              transition: 'all 0.3s ease'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 3
              }}
            >
              {/* Icon with circular background */}
              <Box
                sx={{
                  flexShrink: 0,
                  bgcolor: theme.palette.mode === 'dark' 
                    ? `${config.iconColor}20` 
                    : `${config.iconColor}15`,
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {config.icon ? (
                  <config.icon
                    sx={{
                      fontSize: 32,
                      color: config.iconColor
                    }}
                  />
                ) : (
                  <CircularProgress size={32} color="inherit" />
                )}
              </Box>

              {/* Text content */}
              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="h5" 
                  fontWeight={600}
                  color={config.textColor}
                  gutterBottom
                >
                  {config.title}
                </Typography>
                <Typography
                  variant="body1"
                  color={config.textColor}
                  sx={{ opacity: 0.9 }}
                >
                  {config.subtitle}
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            gap: 2
          }}>
            {localStatus === STRIPE_STATUS.CONNECTED ? (
              <Button
                variant="outlined"
                onClick={handleDisconnectClick}
                disabled={actionInProgress}
                startIcon={actionInProgress ? <CircularProgress size={20} /> : null}
                sx={{
                  backgroundColor: '#fff',
                  color: '#DC2626',
                  borderColor: '#FEE2E2',
                  '&:hover': {
                    backgroundColor: '#FEE2E2',
                    borderColor: '#DC2626',
                    color: '#DC2626'
                  }
                }}
              >
                {actionInProgress ? 'Disconnecting...' : 'Disconnect Account'}
              </Button>
            ) : localStatus === STRIPE_STATUS.RESTRICTED ? (
              <Button
                variant="outlined"
                onClick={handleDisconnectClick}
                disabled={actionInProgress}
                startIcon={actionInProgress ? <CircularProgress size={20} /> : null}
                sx={{
                  backgroundColor: '#fff',
                  color: '#DC2626',
                  borderColor: '#FEE2E2',
                  '&:hover': {
                    backgroundColor: '#FEE2E2',
                    borderColor: '#DC2626',
                    color: '#DC2626'
                  }
                }}
              >
                {actionInProgress ? 'Processing...' : config.buttonText}
              </Button>
            ) : localStatus === STRIPE_STATUS.PENDING ? (
              <Button
                variant="outlined"
                disabled
                sx={{
                  backgroundColor: '#FEF3C7',
                  color: '#D97706',
                  borderColor: '#F59E0B',
                  '&.Mui-disabled': {
                    backgroundColor: '#FEF3C7',
                    color: '#D97706',
                    borderColor: '#F59E0B'
                  }
                }}
              >
                Pending Review...
              </Button>
            ) : localStatus === STRIPE_STATUS.DISABLED ? (
              <Button
                variant="contained"
                onClick={handleConnectClick}
                disabled={actionInProgress}
                startIcon={actionInProgress ? <CircularProgress size={20} /> : null}
                sx={{
                  backgroundColor: '#6B7280',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#4B5563'
                  }
                }}
              >
                {actionInProgress ? 'Processing...' : config.buttonText}
              </Button>
            ) : (
              <Button
                variant="contained"                
                color="main"
                onClick={handleConnectClick}
                disabled={actionInProgress}
                startIcon={actionInProgress ? <CircularProgress size={20} /> : null}                
              >
                {actionInProgress ? 'Processing...' : config.buttonText}
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

// Default props
StripeAccountStatus.defaultProps = {
  status: STRIPE_STATUS.LOADING,
  isLoading: false,
  error: null,
  onConnect: () => console.log('Connect handler not provided'),
  onDisconnect: () => console.log('Disconnect handler not provided'),
  onManageAccount: () => {
    console.log('Manage account handler not provided');
    window.open('https://dashboard.stripe.com', '_blank');
  },
  onRetry: null
};

export default StripeAccountStatus;